/**
 * ISO 14044-aligned number formatting for LCA results.
 *
 * LCA results carry ±10-30% uncertainty at the screening level.
 * Reporting 4+ decimal places implies false precision.
 * Standard practice: 3 significant figures for all impact metrics.
 */

/**
 * Round a number to N significant figures and format with locale separators.
 */
export function toSigFigs(value: number, sigFigs: number = 3): string {
    if (value === 0) return "0";
    const rounded = parseFloat(value.toPrecision(sigFigs));
    if (Math.abs(rounded) >= 1) {
        return rounded.toLocaleString("en-US", { maximumSignificantDigits: sigFigs });
    }
    // For values < 1, toPrecision handles trailing zeros correctly
    return rounded.toPrecision(sigFigs);
}

/**
 * Format an impact value with appropriate significant figures.
 *
 * @param value - Raw numeric value
 * @param type  - Impact category
 * @returns Formatted string (no unit suffix — caller adds unit labels)
 */
export function formatImpact(
    value: number,
    type: "dalys" | "pdfs" | "usd" | "co2e" | "generation"
): string {
    if (value === 0) return "0";

    switch (type) {
        case "usd": {
            const abs = Math.abs(value);
            const sign = value < 0 ? "-" : "";
            if (abs >= 1e9) return `${sign}$${toSigFigs(abs / 1e9)}B`;
            if (abs >= 1e6) return `${sign}$${toSigFigs(abs / 1e6)}M`;
            if (abs >= 1e3) return `${sign}$${toSigFigs(abs / 1e3)}k`;
            return `${sign}$${toSigFigs(abs)}`;
        }

        case "dalys":
            return toSigFigs(value);

        case "pdfs":
            return toSigFigs(value);

        case "co2e": {
            const abs = Math.abs(value);
            if (abs >= 1e6) return `${toSigFigs(value / 1e6)}M`;
            if (abs >= 1e3) return `${toSigFigs(value / 1e3)}k`;
            return toSigFigs(value);
        }

        case "generation": {
            const abs = Math.abs(value);
            if (abs >= 1e3) return `${toSigFigs(value / 1e3)}k`;
            return toSigFigs(value);
        }

        default:
            return toSigFigs(value);
    }
}

/**
 * Format a number in scientific notation using Unicode superscripts.
 * Returns e.g. "1.42×10⁻³" for 0.00142
 */
export function formatScientific(value: number, sigFigs: number = 3): string {
    if (value === 0) return "0";
    const abs = Math.abs(value);
    const exp = Math.floor(Math.log10(abs));
    const mantissa = abs / Math.pow(10, exp);
    const rounded = parseFloat(mantissa.toPrecision(sigFigs));

    const superscripts: Record<string, string> = {
        '0': '\u2070', '1': '\u00B9', '2': '\u00B2', '3': '\u00B3', '4': '\u2074',
        '5': '\u2075', '6': '\u2076', '7': '\u2077', '8': '\u2078', '9': '\u2079',
        '-': '\u207B',
    };
    const supExp = String(exp).split('').map(c => superscripts[c] || c).join('');

    const sign = value < 0 ? '-' : '';
    if (exp === 0) return `${sign}${rounded.toPrecision(sigFigs)}`;
    return `${sign}${rounded.toPrecision(sigFigs)}\u00D710${supExp}`;
}

/**
 * Format a large annual total compactly: e.g. 128K, 1.5M
 */
export function formatCompactValue(value: number): string {
    if (value === 0) return "0";
    const abs = Math.abs(value);
    const sign = value < 0 ? '-' : '';
    if (abs >= 1e9) return `${sign}${toSigFigs(abs / 1e9)}B`;
    if (abs >= 1e6) return `${sign}${toSigFigs(abs / 1e6)}M`;
    if (abs >= 1e3) return `${sign}${toSigFigs(abs / 1e3)}K`;
    return `${sign}${toSigFigs(abs)}`;
}

/**
 * Format currency with 3 significant figures (no abbreviation).
 * Use for comparison tables where you want full numbers with commas.
 */
export function formatCurrencyFull(value: number): string {
    if (value === 0) return "$0";
    const rounded = parseFloat(value.toPrecision(3));
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
    }).format(rounded);
}
